import { Phone, Mail, MapPin, Clock, Euro, Truck, FileText, Recycle } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { CONTACT, CITIES } from "@/data/services";

const INFOS = [
  {
    icon: Euro,
    title: "Mindestauftragswert",
    text: "Privatkunden: 69,00 € netto (82,11 € brutto) · Gewerbe: 99,00 € netto (117,81 € brutto).",
  },
  {
    icon: Truck,
    title: "Anfahrt",
    text: "Im Umkreis von 20 km um Herten inklusive. Darüber hinaus: 0,70 € pro Kilometer.",
  },
  {
    icon: FileText,
    title: "Zahlungsbedingungen",
    text: "Rechnungsstellung nach Leistungserbringung. Zahlungsziel: 7 Tage (bei Stammkunden nach Absprache).",
  },
  {
    icon: Recycle,
    title: "Entsorgung",
    text: "Grünschnitt & Abfälle entsorgen wir auf Wunsch – abgerechnet nach Aufwand.",
  },
];

export default function Kontakt() {
  return (
    <>
      <SEO
        title="Kontakt – ZWILLINGSWERK Herten | Anrufen oder E-Mail schreiben"
        description="ZWILLINGSWERK kontaktieren: Telefon 0176 209 39 646 (Mo–Fr 8–17 Uhr), E-Mail info.zwillingswerk@gmail.com, Johannesstraße 20, 45701 Herten. Anfahrt 20 km inklusive."
      />
      <PageHero
        eyebrow="Kontakt · Ohne Formular, ohne Warteschleife"
        title="Sprechen Sie direkt mit uns."
        description="Ein Anruf genügt: Wir besprechen Ihr Vorhaben, nennen den Festpreisrahmen und vereinbaren kurzfristig einen Termin. Alternativ schreiben Sie uns einfach eine E-Mail."
        testid="kontakt-hero"
      />

      <section className="py-14 bg-[#F8FAF8] border-b border-[#D7E5D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Kontaktdaten */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#122B18] text-white rounded-2xl p-7 space-y-5" data-testid="contact-card">
                <div>
                  <div className="text-xs text-[#A3D9A5] font-bold uppercase flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" /> Telefon (Mobil)
                  </div>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="contact-phone-link"
                    className="text-2xl sm:text-3xl font-black font-display hover:text-[#A3D9A5] transition-colors"
                  >
                    {CONTACT.phoneDisplay}
                  </a>
                  <div className="text-xs text-[#D7E5D9] flex items-center gap-1.5 mt-1">
                    <Clock className="w-3.5 h-3.5" /> Erreichbar: {CONTACT.hours}
                  </div>
                </div>
                <div className="pt-4 border-t border-[#3D8B4D]/60">
                  <div className="text-xs text-[#A3D9A5] font-bold uppercase flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> E-Mail
                  </div>
                  <a
                    href={CONTACT.emailHref}
                    data-testid="contact-mail-link"
                    className="text-lg font-bold hover:text-[#A3D9A5] transition-colors break-all"
                  >
                    {CONTACT.email}
                  </a>
                </div>
                <div className="pt-4 border-t border-[#3D8B4D]/60">
                  <div className="text-xs text-[#A3D9A5] font-bold uppercase flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> Adresse
                  </div>
                  <div className="text-sm font-medium" data-testid="contact-address">
                    {CONTACT.street}
                    <br />
                    {CONTACT.city}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3" data-testid="contact-actions">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="contact-call-button"
                  className="inline-flex justify-center items-center gap-2 bg-[#1E5631] hover:bg-[#154023] text-white font-bold px-4 py-4 rounded-xl transition-colors text-sm"
                >
                  <Phone className="w-4 h-4" /> Anrufen
                </a>
                <a
                  href={CONTACT.emailHref}
                  data-testid="contact-mail-button"
                  className="inline-flex justify-center items-center gap-2 bg-white hover:bg-[#F2F7F2] border-2 border-[#1E5631] text-[#1E5631] font-bold px-4 py-4 rounded-xl transition-colors text-sm"
                >
                  <Mail className="w-4 h-4" /> E-Mail
                </a>
              </div>

              <div className="bg-white border border-[#D7E5D9] rounded-2xl p-5 text-xs text-[#4A634E] space-y-1.5">
                <h2 className="text-sm font-bold text-[#122B18] uppercase tracking-wider">Einsatzgebiet</h2>
                <p>Herten + 20 km Umkreis: {CITIES.join(", ")}.</p>
              </div>
            </div>

            {/* Karte + Wichtige Infos */}
            <div className="lg:col-span-7 space-y-8">
              <div className="rounded-2xl overflow-hidden border-2 border-[#D7E5D9] shadow-md" data-testid="contact-map">
                <iframe
                  title="Karte: ZWILLINGSWERK, Johannesstraße 20, 45701 Herten"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=7.0970%2C51.5760%2C7.1760%2C51.6160&layer=mapnik&marker=51.5965%2C7.1360"
                  className="w-full h-[320px] sm:h-[380px] border-0"
                  loading="lazy"
                />
                <div className="bg-white px-4 py-3 text-xs text-[#4A634E] flex flex-wrap justify-between gap-2">
                  <span>
                    <strong className="text-[#122B18]">ZWILLINGSWERK</strong> · {CONTACT.street}, {CONTACT.city}
                  </span>
                  <a
                    href="https://www.openstreetmap.org/?mlat=51.5965&mlon=7.1360#map=14/51.5965/7.1360"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-map-link"
                    className="text-[#1E5631] font-bold underline"
                  >
                    Route planen
                  </a>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-black text-[#122B18] font-display mb-5">Wichtige Infos im Überblick</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {INFOS.map((i) => (
                    <div
                      key={i.title}
                      className="bg-white border border-[#D7E5D9] rounded-xl p-5 space-y-2"
                      data-testid={`info-${i.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-[#E6F3E6] text-[#1E5631] flex items-center justify-center">
                          <i.icon className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <h3 className="font-bold text-[#122B18] text-sm">{i.title}</h3>
                      </div>
                      <p className="text-xs sm:text-sm text-[#4A634E] leading-relaxed">{i.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
